import { useState, useCallback, useRef, useEffect, createContext, useContext, useMemo, lazy, Suspense, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Menu,
  X,
  Search,
  Bell,
  Home,
  CalendarDays,
  FolderKanban,
  Users,
  Wallet,
  UserCircle,
  Plus,
  Paperclip,
  Mic,
  MicOff,
  Send,
  Loader2,
  MessageSquarePlus,
  Check,
  CheckSquare,
  Trash2,
  ArrowLeft,
  ChevronRight,
  Mail,
  Phone,
  MessageCircle,
  Archive,
  FolderPlus,
  Folder,
  Pencil,
  Copy,
  StickyNote,
  Star,
  Zap,
  Share2,
  Upload,
  CreditCard,
  Settings,
  Link2,
  MessageSquare,
  Link,
  FolderSync,
  Flag,
  Target,
  LifeBuoy,
  Shield,
  Building2,
} from 'lucide-react';

// Helper function to convert icon names to components
function getIconForName(iconName: string, size: number = 16): ReactNode {
  const iconMap: Record<string, ReactNode> = {
    // Navigation icons
    'mail': <Mail size={size} />,
    'phone': <Phone size={size} />,
    'message-circle': <MessageCircle size={size} />,
    'send': <Send size={size} />,
    'users': <Users size={size} />,
    'home': <Home size={size} />,
    'folder': <Folder size={size} />,
    'folder-kanban': <FolderKanban size={size} />,
    'folder-plus': <FolderPlus size={size} />,
    'archive': <Archive size={size} />,
    'calendar': <CalendarDays size={size} />,
    'wallet': <Wallet size={size} />,
    'user': <UserCircle size={size} />,
    'pencil': <Pencil size={size} />,
    'copy': <Copy size={size} />,
    'trash': <Trash2 size={size} />,
    'star': <Star size={size} />,
    // Fast actions (standard)
    'task': <CheckSquare size={size} />,
    'event': <CalendarDays size={size} />,
    'email': <Mail size={size} />,
    'call': <Phone size={size} />,
    'message': <MessageCircle size={size} />,
    'reminder': <Bell size={size} />,
    'note': <StickyNote size={size} />,
    // Settings icons
    'zap': <Zap size={size} />,
    'upload': <Upload size={size} />,
    'credit-card': <CreditCard size={size} />,
    'settings': <Settings size={size} />,
    'life-buoy': <LifeBuoy size={size} />,
    'shield': <Shield size={size} />,
    'building-2': <Building2 size={size} />,
    // Hub icons
    'link-2': <Link2 size={size} />,
    'message-square': <MessageSquare size={size} />,
    'link': <Link size={size} />,
    'folder-sync': <FolderSync size={size} />,
    // Calendar icons
    'flag': <Flag size={size} />,
    'target': <Target size={size} />,
    'bell': <Bell size={size} />,
    // Aliases for consistency across CRM
    'meeting': <Users size={size} />,
    'milestone': <Flag size={size} />,
    'deadline': <Target size={size} />,
    'payment': <Wallet size={size} />,
  };
  return iconMap[iconName] || null;
}
import { whisperClient } from '@business-assistant/shared/services';
import { googleSearchClient, type SearchResponse } from '@business-assistant/shared/services';
import { useChatStore } from '@business-assistant/shared/services';
import { useEntityModal, useTranslation } from '../hooks';
import { renderWidgetByType } from '../components/common/registry/ComponentRouter';
import type { IdPrefix } from '@business-assistant/shared';
import { useAppStore, useProjectsStore } from '@business-assistant/shared';
import { ChatFullScreen, type ChatContext } from '../components/chat/ChatFullScreen';
import type { ViewType } from '../types/appTypes';

// Re-export ViewType for backward compatibility
export type { ViewType } from '../types/appTypes';
import { NotificationService, type Notification } from '../services/NotificationService';
import { SearchDropdown } from '../components/search/SearchDropdown';
import { DevFixPanel } from '../components/devfix/DevFixPanel';
import { NotificationDropdown } from '../components/notifications/NotificationDropdown';
import { GlobalTimerWidget } from '../components/timer/GlobalTimerWidget';
import { FastActionsTrigger } from '../components/actions/FastActions';
import { usePermissions, type ModuleId } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';
import './app-shell.css';

// Lazy load ShareModal to avoid circular dependencies
const ShareModal = lazy(() => import('../components/sharing/ShareModal').then(m => ({ default: m.ShareModal })));
type ResourceType = 'task' | 'file' | 'project' | 'milestone';

// ViewType is now imported from '../types/appTypes' and re-exported above

/**
 * View configuration
 */
interface ViewConfig {
  id: ViewType;
  title: string;
  subtitle: string;
  icon: typeof Home;
  label: string;
}

const VIEW_CONFIG: ViewConfig[] = [
  { id: 'home', title: 'Home', subtitle: 'Chat', icon: Home, label: 'Home' },
  { id: 'today', title: 'Today', subtitle: 'Everything in a list', icon: CalendarDays, label: 'Today' },
  { id: 'projects', title: 'Projects', subtitle: 'Projects list', icon: FolderKanban, label: 'Projects' },
  { id: 'calendar', title: 'Calendar', subtitle: 'Events and deadlines', icon: CalendarDays, label: 'Calendar' },
  { id: 'hub', title: 'Hub', subtitle: 'Key contacts', icon: Users, label: 'Hub' },
  { id: 'accounting', title: 'Accounting', subtitle: 'Operations overview', icon: Wallet, label: 'Accounting' },
  { id: 'settings', title: 'Settings', subtitle: 'Profile and preferences', icon: UserCircle, label: 'Settings' },
];

/**
 * Action for drawer item context menu
 */
export interface DrawerItemAction {
  id: string;
  label: string;
  icon?: string;
  danger?: boolean;
  onClick: () => void;
}

/**
 * Drawer filter item
 *
 * Follows the standard row model:
 * [innerActions] [icon] [label] [fastActions] [3-dot menu]
 */
export interface DrawerFilterItem {
  id: string;
  label: string;
  hint?: string;
  active?: boolean;
  count?: number;
  icon?: string;
  onClick?: () => void;
  /** Inner actions slot (left side: star, avatar, etc.) */
  innerActions?: React.ReactNode;
  /** Show FastActionsTrigger (lightning bolt with dropdown) */
  showFastActions?: boolean;
  /** Callback for fast action clicks (required if showFastActions is true) */
  onFastAction?: (action: string, itemId: string) => void;
  /** Actions shown in 3-dot menu */
  actions?: DrawerItemAction[];
  /** Children items (for folders) */
  children?: DrawerFilterItem[];
  /** Whether item can be dragged (default: true for regular items) */
  draggable?: boolean;
  /** Whether item can accept drops (e.g., folders) */
  isDropTarget?: boolean;
  /** Item type for drag data transfer */
  itemType?: 'chat' | 'client' | 'folder' | 'project';
  /** Callback when something is dropped on this item */
  onDrop?: (e: React.DragEvent) => void;
  /** Callback for drag over (to show drop indicator) */
  onDragOver?: (e: React.DragEvent) => void;
}

/**
 * Drawer section
 */
export interface DrawerSection {
  title: string;
  items: DrawerFilterItem[];
}

/**
 * Breadcrumb item for navigation
 */
export interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

/**
 * Drag and drop event info
 */
export interface DrawerReorderEvent {
  draggedId: string;
  targetId: string;
  position: 'above' | 'below' | 'inside';
  sectionTitle: string;
}

/**
 * AppShell context
 */
interface AppShellContextValue {
  currentView: ViewType;
  setCurrentView: (view: ViewType) => void;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  openPrompt: (prefill?: string) => void;
  promptText: string;
  setPromptText: (text: string) => void;
  webSearchMode: boolean;
  toggleWebSearchMode: () => void;
  showToast: (message: string) => void;
  activeFilters: Record<ViewType, string>;
  setActiveFilter: (view: ViewType, filterId: string) => void;
  // AI status
  isAiProcessing: boolean;
  isRecording: boolean;
  // Chat fullscreen
  chatFullscreenOpen: boolean;
  openChatFullscreen: () => void;
  // Dynamic header and drawer override (for detail views)
  headerTitleOverride: string | null;
  setHeaderTitleOverride: (title: string | null) => void;
  headerBackAction: (() => void) | null;
  setHeaderBackAction: (action: (() => void) | null) => void;
  drawerSectionsOverride: DrawerSection[] | null;
  setDrawerSectionsOverride: (sections: DrawerSection[] | null) => void;
  // Breadcrumb navigation
  headerBreadcrumbs: BreadcrumbItem[] | null;
  setHeaderBreadcrumbs: (breadcrumbs: BreadcrumbItem[] | null) => void;
  // Mobile: bars hidden state (for scroll behavior)
  barsHidden: boolean;
  // Drag and drop callback
  onDrawerReorder: ((event: DrawerReorderEvent) => void) | null;
  setOnDrawerReorder: (callback: ((event: DrawerReorderEvent) => void) | null) => void;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

export function useAppShell() {
  const ctx = useContext(AppShellContext);
  if (!ctx) throw new Error('useAppShell must be used within AppShell');
  return ctx;
}

/**
 * AppShell Props
 */
interface AppShellProps {
  children?: ReactNode;
  /** Content for each view */
  views: Record<ViewType, ReactNode>;
  /** Drawer sections per view */
  drawerContent?: Record<ViewType, DrawerSection[]>;
  /** Callback when filter is selected */
  onFilterSelect?: (view: ViewType, filterId: string) => void;
  /** Callback when prompt is submitted */
  onPromptSubmit?: (text: string, webSearch: boolean) => void;
  /** Callback when search is clicked */
  onSearchClick?: () => void;
  /** Callback when notifications is clicked */
  onNotificationsClick?: () => void;
}

/**
 * AppShell - Main layout container for mobile-first app
 *
 * Architettura AI:
 * - Chat: interfaccia conversazionale per interazioni con l'utente
 * - Agents: esecutori di azioni (invocati dalla chat quando serve)
 *
 * Current view context is passed to AI for contextual commands.
 * E.g., "add this task" knows which project/view we're currently in.
 */
export function AppShell({
  children,
  views,
  drawerContent,
  onFilterSelect,
  onPromptSubmit,
  onSearchClick,
  onNotificationsClick,
}: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentView: globalView } = useAppStore();
  const { canAccess, isAdmin } = usePermissions();
  const { user: authUser } = useAuth();
  const isOwner = authUser?.role === 'owner';
  const [currentView, setCurrentView] = useState<ViewType>(globalView || 'home');

  // Filter VIEW_CONFIG based on permissions
  const accessibleViews = useMemo(() => {
    return VIEW_CONFIG.filter((view) => {
      // Admin can see everything
      if (isAdmin) return true;
      // Map view id to module id for permission check
      const moduleMapping: Record<ViewType, ModuleId | null> = {
        'home': 'home',
        'today': 'home', // "today" is part of home
        'projects': 'projects',
        'calendar': 'calendar',
        'hub': 'hub',
        'accounting': 'accounting',
        'settings': 'settings',
      };
      const moduleId = moduleMapping[view.id];
      if (!moduleId) return true;
      return canAccess(moduleId);
    });
  }, [canAccess, isAdmin]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);

  // Drag and drop state for drawer items
  const [draggedItem, setDraggedItem] = useState<{ id: string; sectionIndex: number; itemIndex: number } | null>(null);
  const [dragOverItem, setDragOverItem] = useState<{ sectionIndex: number; itemIndex: number; position: 'above' | 'below' } | null>(null);
  const [dragOverFolder, setDragOverFolder] = useState<string | null>(null);

  // Mobile: hide header/bottombar on scroll
  const [barsHidden, setBarsHidden] = useState(false);
  const lastScrollY = useRef(0);
  const mainContentRef = useRef<HTMLElement | null>(null);

  // Drawer resize state
  const [drawerWidth, setDrawerWidth] = useState<number>(() => {
    const saved = localStorage.getItem('drawer-width');
    return saved ? parseInt(saved, 10) : 30; // Default 30% of viewport
  });
  const [isResizing, setIsResizing] = useState(false);

  // Sync local state with global store (for URL changes)
  useEffect(() => {
    if (globalView && globalView !== currentView) {
      setCurrentView(globalView);
    }
  }, [globalView]);

  // Sync view with URL pathname
  useEffect(() => {
    const pathToView: Record<string, ViewType> = {
      '/home': 'home',
      '/today': 'today',
      '/projects': 'projects',
      '/calendar': 'calendar',
      '/hub': 'hub',
      '/accounting': 'accounting',
      '/settings': 'settings',
      // Legacy Italian routes (redirect handled elsewhere)
      '/oggi': 'today',
      '/progetti': 'projects',
      '/contabilita': 'accounting',
      '/impostazioni': 'settings',
    };

    const basePath = '/' + location.pathname.split('/')[1];
    const viewFromUrl = pathToView[basePath];

    // Redirect root to home
    if (location.pathname === '/' || location.pathname === '') {
      navigate('/home', { replace: true });
      return;
    }

    if (viewFromUrl && viewFromUrl !== currentView) {
      setCurrentView(viewFromUrl);
    }
  }, [location.pathname, navigate]);

  // Sync accounting section filter from URL
  useEffect(() => {
    if (currentView === 'accounting' || location.pathname.includes('accounting')) {
      const params = new URLSearchParams(location.search);
      const section = params.get('section');
      if (section && activeFilters.accounting !== section) {
        setActiveFilters((prev) => ({ ...prev, accounting: section }));
      }
    }
  }, [location, currentView]);

  // Sync settings section filter from URL path (e.g., /settings/personal -> 'personal')
  useEffect(() => {
    if (currentView === 'settings' || location.pathname.includes('/settings/')) {
      const match = location.pathname.match(/\/settings\/(\w+)/);
      const section = match?.[1];
      if (section && activeFilters.settings !== section) {
        setActiveFilters((prev) => ({ ...prev, settings: section }));
      }
    }
  }, [location.pathname, currentView]);

  const [promptText, setPromptText] = useState('');
  const [webSearchMode, setWebSearchMode] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<ViewType, string>>({
    home: 'all',
    today: 'all',
    projects: 'all',
    calendar: 'all',
    hub: 'all',
    accounting: 'dashboard',
    settings: 'all',
  });

  // Chat fullscreen state
  const [chatFullscreenOpen, setChatFullscreenOpen] = useState(false);

  // Dynamic header and drawer overrides (for detail views like project detail)
  const [headerTitleOverride, setHeaderTitleOverride] = useState<string | null>(null);
  const [headerBackAction, setHeaderBackAction] = useState<(() => void) | null>(null);
  const [drawerSectionsOverride, setDrawerSectionsOverride] = useState<DrawerSection[] | null>(null);
  const [headerBreadcrumbs, setHeaderBreadcrumbs] = useState<BreadcrumbItem[] | null>(null);
  const [onDrawerReorder, setOnDrawerReorder] = useState<((event: DrawerReorderEvent) => void) | null>(null);

  // Reset drawer sections when view changes (prevents stale data between views)
  const prevViewRef = useRef<ViewType>(currentView);
  useEffect(() => {
    if (prevViewRef.current !== currentView) {
      // View changed - reset sections so the new view can set its own
      setDrawerSectionsOverride(null);
      prevViewRef.current = currentView;
    }
  }, [currentView]);

  // Search and Notifications state
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareResource, setShareResource] = useState<{
    type: ResourceType;
    id: string;
    title?: string;
  } | null>(null);

  // AI state
  const [isAiProcessing, setIsAiProcessing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [whisperAvailable, setWhisperAvailable] = useState<boolean | null>(null);
  const recorderRef = useRef<ReturnType<typeof whisperClient.createAudioRecorder> | null>(null);
  const recordingTimerRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Chat store
  const chatStore = useChatStore();

  // Check AI services on mount
  useEffect(() => {
    async function checkServices() {
      const whisperOk = await whisperClient.checkHealth();
      setWhisperAvailable(whisperOk);
    }
    checkServices();
  }, []);

  // Subscribe to notification changes
  useEffect(() => {
    setUnreadCount(NotificationService.getUnreadCount());

    const unsubscribe = NotificationService.subscribe(() => {
      setUnreadCount(NotificationService.getUnreadCount());
    });

    return unsubscribe;
  }, []);

  // Mobile: hide/show header and bottombar on scroll
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    const handleScroll = () => {
      const mainContent = mainContentRef.current;
      if (!mainContent) return;

      const currentScrollY = mainContent.scrollTop;
      const scrollDelta = currentScrollY - lastScrollY.current;

      // Only trigger if scroll delta is significant (>5px)
      if (Math.abs(scrollDelta) > 5) {
        if (scrollDelta > 0 && currentScrollY > 60) {
          // Scrolling down - hide bars
          setBarsHidden(true);
        } else if (scrollDelta < 0) {
          // Scrolling up - show bars
          setBarsHidden(false);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    const mainContent = mainContentRef.current;
    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll, { passive: true });
    }

    // Also listen for resize to handle orientation changes
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setBarsHidden(false);
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      if (mainContent) {
        mainContent.removeEventListener('scroll', handleScroll);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Build chat context from current view
  const currentViewConfig = VIEW_CONFIG.find((v) => v.id === currentView) || VIEW_CONFIG[0];
  const chatContext: ChatContext = {
    view: currentView,
    viewTitle: currentViewConfig.title,
    activeFilter: activeFilters[currentView],
  };

  // NOTE: PROJECTS_SECTIONS moved after showToast/closeDrawer to avoid temporal dead zone

  // Accounting sub-sections
  const ACCOUNTING_SECTIONS: DrawerSection[] = [
    {
      title: 'Sections',
      items: [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'customers', label: 'Customers' },
        { id: 'invoices', label: 'Invoices' },
        { id: 'movements', label: 'Journal' },
        { id: 'banking', label: 'Banking' },
        { id: 'chartofaccounts', label: 'Chart of Accounts' },
        { id: 'duedates', label: 'Due Dates' },
        { id: 'reports', label: 'Reports' },
      ],
    },
  ];

  // Settings sub-sections - filtered by user role
  const SETTINGS_SECTIONS: DrawerSection[] = useMemo(() => {
    const baseItems = [
      { id: 'personal', label: 'Personal Info', hint: 'Profile and preferences', icon: 'user' },
      { id: 'security', label: 'Security', hint: 'Password and authentication', icon: 'shield' },
    ];

    const ownerItems = [
      { id: 'company', label: 'Company Info', hint: 'Business details and billing', icon: 'building-2' },
      { id: 'integrations', label: 'Integrations', hint: 'Connected services', icon: 'zap' },
      { id: 'subscription', label: 'Subscription', hint: 'Plan and team', icon: 'credit-card' },
      { id: 'imports', label: 'Imports', hint: 'Migrate data from other apps', icon: 'upload' },
    ];

    const supportItem = { id: 'support', label: 'Support', hint: 'Get help and report issues', icon: 'life-buoy' };

    return [
      {
        title: 'Settings',
        items: [
          ...baseItems,
          ...(isOwner ? ownerItems : []),
          supportItem,
        ],
      },
    ];
  }, [isOwner]);

  // Get the header title - MOVED HERE to avoid temporal dead zone in handleShare
  const getHeaderTitle = (): string => {
    // Use override if set (for detail views like project detail)
    if (headerTitleOverride) {
      return headerTitleOverride;
    }

    const activeSection = activeFilters[currentView];

    if (currentView === 'accounting' && activeSection) {
      const section = ACCOUNTING_SECTIONS[0]?.items.find(item => item.id === activeSection);
      if (section) return section.label;
    }

    if (currentView === 'settings' && activeSection) {
      const section = SETTINGS_SECTIONS[0]?.items.find(item => item.id === activeSection);
      if (section) return section.label;
    }

    return currentViewConfig.title;
  };

  const headerTitle = getHeaderTitle();

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Drawer resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidthPx = e.clientX;
      const viewportWidth = window.innerWidth;
      const newWidthPercent = (newWidthPx / viewportWidth) * 100;

      // Limit between 15% and 50% of viewport
      const clampedWidth = Math.min(Math.max(newWidthPercent, 15), 50);
      setDrawerWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      // Save to localStorage
      localStorage.setItem('drawer-width', drawerWidth.toString());
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, drawerWidth]);

  const openChatFullscreen = useCallback(() => {
    setChatFullscreenOpen(true);
  }, []);

  const closeChatFullscreen = useCallback(() => {
    setChatFullscreenOpen(false);
    // Reset session for next chat
    chatStore.setActiveSession(null);
  }, [chatStore]);

  const openPrompt = useCallback((prefill?: string) => {
    if (prefill) setPromptText(prefill);
    openChatFullscreen();
  }, [openChatFullscreen]);

  const toggleWebSearchMode = useCallback(() => {
    setWebSearchMode((prev) => !prev);
  }, []);

  const showToast = useCallback((message: string) => {
    setToastMessage(message);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 1400);
  }, []);

  // Projects sub-sections - computed from store (defined here after showToast/closeDrawer)
  const storeProjects = useProjectsStore((state) => state.projects);

  // Helper to get initials from client name
  const getInitials = (name: string) => {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  // Handler for client fast actions
  const handleClientFastAction = useCallback((action: string, clientId: string) => {
    const clientName = clientId.replace('client:', '');
    if (action === 'new-project') {
      // Navigate to create project with client pre-selected
      navigate(`/projects?newProject=true&client=${encodeURIComponent(clientName)}`);
    } else if (action === 'edit-client') {
      // Navigate to hub contacts with this client
      navigate(`/hub/contacts?search=${encodeURIComponent(clientName)}`);
    }
    showToast(`Action: ${action} for ${clientName}`);
    closeDrawer();
  }, [navigate, showToast, closeDrawer]);

  const PROJECTS_SECTIONS: DrawerSection[] = useMemo(() => {
    const activeProjects = storeProjects.filter((p) => p.status === 'attivo');
    const archivedCount = storeProjects.filter((p) => p.status === 'archiviato').length;

    // Get unique clients
    const clientCounts: Record<string, number> = {};
    activeProjects.forEach((p) => {
      if (p.clientName) {
        clientCounts[p.clientName] = (clientCounts[p.clientName] || 0) + 1;
      }
    });
    const uniqueClients = Object.keys(clientCounts).sort();

    const sections: DrawerSection[] = [
      {
        title: 'Organize',
        items: [
          { id: 'archiviato', label: 'Archive', icon: 'archive', count: archivedCount, isDropTarget: true, draggable: false },
          { id: 'create-folder', label: '+ Create folder', icon: 'folder-plus', draggable: false },
        ],
      },
    ];

    // Add Clienti section with full row model
    if (uniqueClients.length > 0) {
      sections.push({
        title: 'Clients',
        items: uniqueClients.map((clientName) => ({
          id: `client:${clientName}`,
          label: clientName,
          count: clientCounts[clientName],
          itemType: 'client' as const,
          // Inner actions: star + avatar with initials
          innerActions: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Star size={14} style={{ color: 'var(--text-muted)', cursor: 'pointer' }} />
              <div style={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                background: 'var(--accent-color, #3b82f6)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.625rem',
                fontWeight: 600,
              }}>
                {getInitials(clientName)}
              </div>
            </div>
          ),
          showFastActions: true,
          onFastAction: handleClientFastAction,
          actions: [
            {
              id: 'new-project',
              label: t('projects.newProject'),
              icon: 'plus',
              onClick: () => handleClientFastAction('new-project', `client:${clientName}`),
            },
            {
              id: 'edit-client',
              label: t('common.edit'),
              icon: 'pencil',
              onClick: () => handleClientFastAction('edit-client', `client:${clientName}`),
            },
            {
              id: 'move-to-folder',
              label: t('common.moveToFolder'),
              icon: 'folder',
              onClick: () => handleClientFastAction('move-to-folder', `client:${clientName}`),
            },
          ],
        })),
      });
    } else {
      sections.push({
        title: t('accounting.sections.customers'),
        items: [
          { id: 'no-clients', label: t('common.noResults'), hint: '' },
        ],
      });
    }

    return sections;
  }, [storeProjects, handleClientFastAction]);

  // Drag and drop handlers for drawer items
  const handleDragStart = useCallback((e: React.DragEvent, sectionIndex: number, itemIndex: number, item: DrawerFilterItem) => {
    // Don't allow dragging items that are explicitly not draggable
    if (item.draggable === false) {
      e.preventDefault();
      return;
    }

    setDraggedItem({ id: item.id, sectionIndex, itemIndex });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: item.id, sectionIndex, itemIndex, type: item.itemType }));

    // Add drag image styling
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  }, []);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    setDraggedItem(null);
    setDragOverItem(null);
    setDragOverFolder(null);

    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, sectionIndex: number, itemIndex: number, item: DrawerFilterItem) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedItem) return;

    // If dragging over a folder, highlight it
    if (item.isDropTarget || item.icon === 'folder') {
      setDragOverFolder(item.id);
      setDragOverItem(null);
      return;
    }

    // Determine if we're above or below the middle of the item
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? 'above' : 'below';

    setDragOverItem({ sectionIndex, itemIndex, position });
    setDragOverFolder(null);
  }, [draggedItem]);

  const handleDragLeave = useCallback(() => {
    setDragOverFolder(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetSectionIndex: number, targetItemIndex: number, targetItem: DrawerFilterItem) => {
    e.preventDefault();

    if (!draggedItem) return;

    const isDropOnFolder = targetItem.isDropTarget || targetItem.icon === 'folder';
    const position = dragOverItem?.position || 'below';

    // Build reorder event
    const reorderEvent: DrawerReorderEvent = {
      draggedId: draggedItem.id,
      targetId: targetItem.id,
      position: isDropOnFolder ? 'inside' : position,
      sectionTitle: '', // Consumer can determine from IDs
    };

    // Call the callback if set
    if (onDrawerReorder) {
      onDrawerReorder(reorderEvent);
    } else {
      // Fallback toast if no callback
      if (isDropOnFolder) {
        showToast(`Moved to "${targetItem.label}"`);
      } else {
        showToast(`Order updated`);
      }
    }

    setDraggedItem(null);
    setDragOverItem(null);
    setDragOverFolder(null);
  }, [draggedItem, dragOverItem, showToast, onDrawerReorder]);

  // Handle share button click - contextual based on current view
  const handleShare = useCallback(() => {
    // Determine resource based on current view and context
    let resource: { type: ResourceType; id: string; title?: string } | null = null;

    if (currentView === 'home') {
      // Share current chat session
      const activeSession = chatStore.getActiveSession();
      if (activeSession) {
        resource = {
          type: 'project', // Chat sessions are shared as projects for now
          id: activeSession.id,
          title: activeSession.title || 'Chat',
        };
      }
    } else if (currentView === 'projects') {
      // Share current project/task context
      resource = {
        type: 'project',
        id: 'current', // Will be replaced with actual project ID from context
        title: headerTitle,
      };
    }

    if (resource) {
      setShareResource(resource);
      setShareModalOpen(true);
    } else {
      showToast('Select an item to share');
    }
  }, [currentView, chatStore, headerTitle, showToast]);

  const setActiveFilter = useCallback((view: ViewType, filterId: string) => {
    setActiveFilters((prev) => ({ ...prev, [view]: filterId }));
    onFilterSelect?.(view, filterId);
  }, [onFilterSelect]);

  // Build system prompt with context
  const buildSystemPrompt = useCallback(() => {
    return `You are an intelligent business assistant. You help users manage tasks, projects, emails, invoices, and communications.

CURRENT CONTEXT:
- View: ${chatContext.viewTitle}
- Filter: ${chatContext.activeFilter || 'all'}
${chatContext.selectedEntityId ? `- Selected item: ${chatContext.selectedEntityId}` : ''}

RULES:
1. Respond in a concise and professional manner.
2. If the user asks to do something (create task, send email, etc.), describe what you would do and ask for confirmation.
3. When you say "this" or "here", refer to the current context (view, filter, selected item).
4. For complex actions, break down into steps and ask for confirmation before proceeding.
5. If you don't have enough information, ask for clarification.

CONTEXTUAL COMMAND EXAMPLES:
- "add a task here" → creates task in current view/project
- "send a reminder to this contact" → uses selected contact
- "invoice this" → creates invoice for current element`;
  }, [chatContext]);

  // Build enhanced system prompt with web search results (RAG pattern)
  const buildSearchEnhancedPrompt = useCallback((searchResults: SearchResponse) => {
    return `You are an intelligent business assistant. You help users manage tasks, projects, emails, invoices, and communications.

WEB SEARCH RESULTS for: "${searchResults.query}"

${searchResults.results.map((r, i) => `[${i + 1}] ${r.title}
    Source: ${r.displayLink}
    ${r.snippet}
`).join('\n')}

IMPORTANT INSTRUCTIONS:
1. Analyze the search results provided above
2. Synthesize the most relevant information to answer the user's question
3. Respond in a professional and concise manner
4. ALWAYS CITE sources using numbers [1], [2], [3], etc.
5. If the results don't contain enough information, say so clearly
6. Don't make up information not present in the results

USER CONTEXT:
- Current view: ${chatContext.viewTitle}
- Active filter: ${chatContext.activeFilter || 'all'}

Answer the user's question based on the web results provided.`;
  }, [chatContext]);

  // Handle prompt submit
  const handlePromptSubmit = useCallback(() => {
    if (!promptText.trim()) {
      showToast(t('home.inputPlaceholder'));
      return;
    }

    // Use fallback behavior
    onPromptSubmit?.(promptText, webSearchMode);
    showToast(`${webSearchMode ? 'Web' : 'Action'} - ${promptText}`);
    setPromptText('');
  }, [promptText, webSearchMode, onPromptSubmit, showToast, t]);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!whisperAvailable) {
      showToast("Whisper not available");
      return;
    }

    try {
      recorderRef.current = whisperClient.createAudioRecorder();
      await recorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimerRef.current = window.setInterval(() => {
        setRecordingTime((t) => t + 1);
      }, 1000);
    } catch (error) {
      console.error("Recording error:", error);
      showToast(t('errors.microphonePermission'));
    }
  }, [whisperAvailable, showToast]);

  // Stop recording and transcribe
  const stopRecording = useCallback(async () => {
    if (!recorderRef.current) return;

    // Stop timer
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    setIsRecording(false);
    setIsAiProcessing(true);

    try {
      const audioBlob = await recorderRef.current.stop();
      recorderRef.current = null;

      showToast("Transcription in progress...");

      // Transcribe with Whisper
      const transcription = await whisperClient.transcribeBlob(audioBlob, "it");

      if (transcription.trim()) {
        // Set as prompt text (no AI processing)
        setPromptText(transcription.trim());
        showToast("Transcription complete");
      } else {
        showToast(t('common.noResults'));
      }
      setIsAiProcessing(false);
    } catch (error) {
      console.error("Transcription error:", error);
      showToast(t('errors.transcriptionFailed'));
      setIsAiProcessing(false);
    }
  }, [showToast, t]);

  // Toggle recording
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // Handle file attachment
  const handleFileAttach = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPromptText((prev) => prev + ` [attachment: ${file.name}]`);
      showToast(`Attachment: ${file.name}`);
    }
    e.target.value = '';
  }, [showToast]);

  const handleViewChange = useCallback((view: ViewType) => {
    // Map view to URL (English routes)
    const viewToUrl: Record<ViewType, string> = {
      home: '/home',
      today: '/today',
      projects: '/projects',
      calendar: '/calendar',
      hub: '/hub',
      accounting: '/accounting',
      settings: '/settings',
    };

    navigate(viewToUrl[view] || '/home');
    setCurrentView(view);
    closeDrawer();
  }, [closeDrawer, navigate]);

  const handleDrawerItemClick = useCallback((view: ViewType, filterId: string) => {
    // For projects view, navigate to URL for deep linking
    if (view === 'projects') {
      // Extract project ID from current URL if we're in a project detail
      const projectMatch = location.pathname.match(/\/projects\/([^/]+)/);
      if (projectMatch) {
        const projectId = projectMatch[1];
        // Always include section in URL (dashboard is explicit)
        navigate(`/projects/${projectId}/${filterId}`);
        closeDrawer();
        return; // Exit early - URL change will update state
      }
    }

    // For hub view, navigate to channel URL
    if (view === 'hub') {
      const channelMap: Record<string, string> = {
        email: '/hub/email',
        whatsapp: '/hub/whatsapp',
        telegram: '/hub/telegram',
        phone: '/hub/calls',
        sms: '/hub/sms',
        meet: '/hub/meet',
        contacts: '/hub/contacts',
      };
      const channelUrl = channelMap[filterId];
      if (channelUrl) {
        navigate(channelUrl);
        closeDrawer();
        return;
      }
    }

    setActiveFilter(view, filterId);

    // For accounting view, navigate with section parameter
    if (view === 'accounting') {
      navigate(`/accounting?section=${filterId}`);
    }

    // For settings view, navigate with section path
    if (view === 'settings') {
      navigate(`/settings/${filterId}`);
    }

    closeDrawer();
  }, [setActiveFilter, closeDrawer, navigate, location.pathname]);

  // Format recording time
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  // Use hardcoded sections for specific views, or override if set
  // NOTE: ACCOUNTING_SECTIONS and SETTINGS_SECTIONS are defined earlier to avoid temporal dead zone
  const currentDrawerSections = drawerSectionsOverride
    ? drawerSectionsOverride
    : currentView === 'projects'
    ? PROJECTS_SECTIONS
    : currentView === 'accounting'
    ? ACCOUNTING_SECTIONS
    : currentView === 'settings'
    ? SETTINGS_SECTIONS
    : (drawerContent?.[currentView] || []);

  // NOTE: getHeaderTitle and headerTitle are now defined earlier (after chatContext)
  // to avoid temporal dead zone error in handleShare callback

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue: AppShellContextValue = useMemo(() => ({
    currentView,
    setCurrentView: handleViewChange,
    drawerOpen,
    openDrawer,
    closeDrawer,
    openPrompt,
    promptText,
    setPromptText,
    webSearchMode,
    toggleWebSearchMode,
    showToast,
    activeFilters,
    setActiveFilter,
    isAiProcessing,
    isRecording,
    chatFullscreenOpen,
    openChatFullscreen,
    // Dynamic overrides for detail views
    headerTitleOverride,
    setHeaderTitleOverride,
    headerBackAction,
    setHeaderBackAction,
    drawerSectionsOverride,
    setDrawerSectionsOverride,
    // Breadcrumb navigation
    headerBreadcrumbs,
    setHeaderBreadcrumbs,
    // Mobile: bars hidden state
    barsHidden,
    // Drag and drop callback
    onDrawerReorder,
    setOnDrawerReorder,
  }), [
    currentView,
    handleViewChange,
    drawerOpen,
    openDrawer,
    closeDrawer,
    openPrompt,
    promptText,
    setPromptText,
    webSearchMode,
    toggleWebSearchMode,
    showToast,
    activeFilters,
    setActiveFilter,
    isAiProcessing,
    isRecording,
    chatFullscreenOpen,
    openChatFullscreen,
    headerTitleOverride,
    setHeaderTitleOverride,
    headerBackAction,
    setHeaderBackAction,
    drawerSectionsOverride,
    setDrawerSectionsOverride,
    headerBreadcrumbs,
    setHeaderBreadcrumbs,
    barsHidden,
    onDrawerReorder,
    setOnDrawerReorder,
  ]);

  return (
    <AppShellContext.Provider value={contextValue}>
      <div className="app-shell">
        {/* Drawer */}
        <aside
          className={`drawer ${!drawerOpen ? 'closed' : ''}`}
          aria-label="Section menu"
          style={{
            width: window.innerWidth > 768 ? `${drawerWidth}%` : undefined,
            userSelect: isResizing ? 'none' : undefined,
          }}
        >
          <div className="drawer__header">
            <button className="drawer__close" onClick={closeDrawer} aria-label={t('common.close')}>
              <X size={20} />
            </button>
            <h2 className="drawer__title">
              {currentView === 'home' ? 'History' : currentViewConfig.title}
            </h2>
            {/* Home view: sidebar actions */}
            {currentView === 'home' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginLeft: 'auto' }}>
                <button
                  onClick={() => {
                    // TODO: Implement search in sidebar
                    console.log('Search in chat history');
                  }}
                  style={{
                    padding: '0.375rem',
                    paddingRight: 0,
                    marginRight: '-0.25rem',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '0.25rem',
                  }}
                  title="Search in chats"
                >
                  <Search size={18} />
                </button>
              </div>
            )}
          </div>
          <div className="drawer__content">
            {/* All views: show drawer sections (managed by each view via setDrawerSectionsOverride) */}
            {currentDrawerSections.map((section, sectionIndex) => (
                <div key={section.title} className="drawer__section">
                  {section.title && (
                    <div className="drawer__section-title">{section.title}</div>
                  )}
                  {section.items.map((item, itemIndex) => (
                    <div key={item.id}>
                      {/* Drop indicator above item */}
                      {dragOverItem?.sectionIndex === sectionIndex && dragOverItem?.itemIndex === itemIndex && dragOverItem?.position === 'above' && (
                        <div style={{ height: '2px', background: 'var(--accent-color, #3b82f6)', margin: '0 0.5rem' }} />
                      )}
                      <div
                        className={`drawer__item ${item.active ?? (activeFilters[currentView] === item.id) ? 'active' : ''} ${dragOverFolder === item.id ? 'drawer__item--drop-target' : ''}`}
                        onClick={() => item.onClick ? item.onClick() : handleDrawerItemClick(currentView, item.id)}
                        style={{
                          position: 'relative',
                          opacity: draggedItem?.id === item.id ? 0.5 : 1,
                          background: dragOverFolder === item.id ? 'var(--accent-light, rgba(59, 130, 246, 0.1))' : undefined,
                          transition: 'background 0.15s, opacity 0.15s',
                        }}
                        draggable={item.draggable !== false}
                        onDragStart={(e) => handleDragStart(e, sectionIndex, itemIndex, item)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, sectionIndex, itemIndex, item)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, sectionIndex, itemIndex, item)}
                      >
                        {/* Inner actions slot (left side) */}
                        {item.innerActions && (
                          <div
                            className="drawer__item-inner-actions"
                            onClick={(e) => e.stopPropagation()}
                            style={{ display: 'flex', alignItems: 'center', gap: '4px', marginRight: '8px', flexShrink: 0 }}
                          >
                            {item.innerActions}
                          </div>
                        )}
                        <div className="drawer__item-content">
                          <div className="drawer__item-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            {item.icon && (
                              <span style={{ opacity: 0.7 }}>{getIconForName(item.icon)}</span>
                            )}
                            {item.children && (
                              <ChevronRight
                                size={14}
                                style={{
                                  transform: item.active ? 'rotate(90deg)' : 'none',
                                  transition: 'transform 0.2s',
                                  opacity: 0.5,
                                }}
                              />
                            )}
                            {item.label}
                            {item.count !== undefined && (
                              <span style={{ marginLeft: '0.5rem', color: 'var(--text-muted, #9ca3af)', fontSize: '0.875rem' }}>
                                ({item.count})
                              </span>
                            )}
                          </div>
                          {item.hint && <div className="drawer__item-hint">{item.hint}</div>}
                        </div>
                        {/* Fast actions trigger (lightning bolt with dropdown) */}
                        {item.showFastActions && item.onFastAction && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <FastActionsTrigger
                              entityId={item.id}
                              onAction={(action, id) => item.onFastAction?.(action, id)}
                              size="sm"
                            />
                          </div>
                        )}
                        {/* Actions menu button (3-dot) - always visible */}
                        {item.actions && item.actions.length > 0 && (
                          <button
                            className="drawer__item-actions-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenuId(openActionMenuId === item.id ? null : item.id);
                            }}
                            style={{
                              padding: '0.25rem',
                              paddingRight: 0,
                              marginRight: '-0.25rem',
                              border: 'none',
                              background: 'transparent',
                              cursor: 'pointer',
                              opacity: 0.6,
                              display: 'flex',
                              alignItems: 'center',
                              borderRadius: '0.25rem',
                              color: 'var(--text-muted)',
                              transition: 'opacity 0.2s',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.6'}
                          >
                            <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor">
                              <circle cx="12" cy="5" r="2" />
                              <circle cx="12" cy="12" r="2" />
                              <circle cx="12" cy="19" r="2" />
                            </svg>
                          </button>
                        )}
                        {/* Actions dropdown menu */}
                        {openActionMenuId === item.id && item.actions && (
                          <div
                            style={{
                              position: 'absolute',
                              right: '0.5rem',
                              top: '100%',
                              backgroundColor: 'var(--background, #fff)',
                              border: '1px solid var(--border, #e5e7eb)',
                              borderRadius: '0.5rem',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              zIndex: 100,
                              minWidth: '160px',
                              overflow: 'hidden',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {item.actions.map((action) => (
                              <button
                                key={action.id}
                                onClick={() => {
                                  action.onClick();
                                  setOpenActionMenuId(null);
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem 0.75rem',
                                  border: 'none',
                                  background: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  fontSize: '0.875rem',
                                  color: action.danger ? 'var(--danger, #ef4444)' : 'inherit',
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--surface, #f5f5f5)'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                {action.icon && <span style={{ opacity: 0.7 }}>{getIconForName(action.icon)}</span>}
                                {action.label}
                              </button>
                            ))}
                          </div>
                        )}
                        {!item.actions && (
                          <div className="drawer__item-pill">
                            {activeFilters[currentView] === item.id ? '✓' : '→'}
                          </div>
                        )}
                      </div>
                      {/* Drop indicator below item */}
                      {dragOverItem?.sectionIndex === sectionIndex && dragOverItem?.itemIndex === itemIndex && dragOverItem?.position === 'below' && (
                        <div style={{ height: '2px', background: 'var(--accent-color, #3b82f6)', margin: '0 0.5rem' }} />
                      )}
                      {/* Children items (for folders) */}
                      {item.children && item.active && (
                        <div style={{ paddingLeft: '1.5rem' }}>
                          {item.children.map((child) => (
                            <div
                              key={child.id}
                              className={`drawer__item ${child.active ? 'active' : ''} ${child.isDropTarget ? 'drop-target' : ''}`}
                              onClick={() => child.onClick ? child.onClick() : handleDrawerItemClick(currentView, child.id)}
                              onDragOver={(e) => {
                                if (child.onDragOver || child.isDropTarget) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  e.currentTarget.classList.add('drag-over');
                                  child.onDragOver?.(e);
                                }
                              }}
                              onDragLeave={(e) => {
                                e.currentTarget.classList.remove('drag-over');
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                e.currentTarget.classList.remove('drag-over');
                                child.onDrop?.(e);
                              }}
                              style={{ fontSize: '0.875rem' }}
                            >
                              <div className="drawer__item-content">
                                <div className="drawer__item-name">{child.label}</div>
                                {child.hint && <div className="drawer__item-hint">{child.hint}</div>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))}
          </div>

          {/* Resize handle - desktop only */}
          {window.innerWidth > 768 && (
            <div
              onMouseDown={handleResizeStart}
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: '5px',
                cursor: 'col-resize',
                background: isResizing ? '#3b82f6' : 'transparent',
                transition: isResizing ? 'none' : 'background 0.2s',
                zIndex: 10,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#e5e7eb';
              }}
              onMouseLeave={(e) => {
                if (!isResizing) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            />
          )}
        </aside>

        {/* Main area */}
        <div className="main-area">
          {/* Header - always visible, single layout for all views */}
          <header className={`header ${barsHidden ? 'header--hidden' : ''}`}>
            {headerBackAction ? (
              <button className="header__back" onClick={headerBackAction} aria-label="Back">
                <ArrowLeft size={20} />
              </button>
            ) : (
              <button className="header__hamburger" onClick={openDrawer} aria-label={t('common.search')}>
                <Menu size={20} />
              </button>
            )}
            {/* Breadcrumb navigation or simple title */}
            {headerBreadcrumbs && headerBreadcrumbs.length > 0 ? (
              <nav className="header__breadcrumbs" aria-label="Breadcrumb">
                {headerBreadcrumbs.map((crumb, index) => (
                  <span key={index} className="header__breadcrumb-item">
                    {index > 0 && (
                      <ChevronRight size={14} className="header__breadcrumb-separator" />
                    )}
                    {crumb.onClick ? (
                      <button
                        className="header__breadcrumb-link"
                        onClick={crumb.onClick}
                      >
                        {crumb.label}
                      </button>
                    ) : (
                      <span className="header__breadcrumb-current">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            ) : (
              <h1 className="header__title">{headerTitle}</h1>
            )}
            <div className="header__actions">
              {/* Home view: New chat button */}
              {currentView === 'home' && (
                <button
                  className="header__action"
                  onClick={() => {
                    chatStore.setActiveSession(null);
                    navigate('/home');
                  }}
                  aria-label={t('sidebar.newChat')}
                  title={t('sidebar.newChat')}
                >
                  <MessageSquarePlus size={18} />
                </button>
              )}
              {/* Share button */}
              <button
                className="header__action"
                onClick={handleShare}
                aria-label="Share"
                title="Share"
              >
                <Share2 size={18} />
              </button>
              <button
                className="header__action"
                onClick={() => {
                  setSearchOpen(!searchOpen);
                  setNotificationsOpen(false);
                }}
                aria-label="Search"
              >
                <Search size={18} />
              </button>
              <button
                className="header__action"
                onClick={() => {
                  setNotificationsOpen(!notificationsOpen);
                  setSearchOpen(false);
                }}
                aria-label="Notifications"
                style={{ position: 'relative', paddingRight: 0, marginRight: '-0.25rem' }}
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: '#ef4444',
                    color: 'white',
                    borderRadius: '50%',
                    width: '18px',
                    height: '18px',
                    fontSize: '10px',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </div>
          </header>

          {/* Search Dropdown */}
          {searchOpen && <SearchDropdown onClose={() => setSearchOpen(false)} />}

          {/* Notifications Dropdown */}
          {notificationsOpen && <NotificationDropdown onClose={() => setNotificationsOpen(false)} />}

          {/* Share Modal */}
          {shareResource && (
            <Suspense fallback={null}>
              <ShareModal
                isOpen={shareModalOpen}
                onClose={() => {
                  setShareModalOpen(false);
                  setShareResource(null);
                }}
                resourceType={shareResource.type}
                resourceId={shareResource.id}
                resourceTitle={shareResource.title}
              />
            </Suspense>
          )}

          {/* Main content - Only render active view to trigger useEffect on navigation */}
          <main className="main-content" ref={mainContentRef}>
            <section
              key={currentView}
              className="view active"
              data-view={currentView}
            >
              <div className="view__body">{views[currentView]}</div>
            </section>
          </main>

          {/* FAB - Hidden on Home view, transforms to X when chat open */}
          {currentView !== 'home' && (
            <button
              className="fab"
              onClick={() => chatFullscreenOpen ? closeChatFullscreen() : openChatFullscreen()}
              aria-label={chatFullscreenOpen ? t('common.close') : t('nav.home')}
            >
              {chatFullscreenOpen ? <X size={20} /> : <Plus size={20} />}
            </button>
          )}

          {/* Bottom nav - filtered by permissions */}
          <nav className={`bottombar ${barsHidden ? 'bottombar--hidden' : ''}`} aria-label="Main navigation">
            {accessibleViews.map((view) => {
              const Icon = view.icon;
              return (
                <button
                  key={view.id}
                  className={`bottombar__tab ${currentView === view.id ? 'active' : ''}`}
                  onClick={() => handleViewChange(view.id)}
                  aria-current={currentView === view.id ? 'page' : undefined}
                >
                  <Icon size={18} />
                  <span>{view.label}</span>
                  <span className="bottombar__dot" aria-hidden="true" />
                </button>
              );
            })}
          </nav>
        </div>

        {/* Scrim */}
        <div
          className={`scrim ${drawerOpen ? 'show' : ''}`}
          onClick={closeDrawer}
          aria-hidden="true"
        />

        {/* Chat Fullscreen */}
        <ChatFullScreen
          isOpen={chatFullscreenOpen}
          onClose={closeChatFullscreen}
          context={chatContext}
          inputText={promptText}
          onInputChange={setPromptText}
          onSend={handlePromptSubmit}
          isRecording={isRecording}
          recordingTime={recordingTime}
          onToggleRecording={toggleRecording}
          isProcessing={isAiProcessing}
          whisperAvailable={whisperAvailable}
          onFileAttach={handleFileAttach}
        />

        {/* Hidden file input for fullscreen */}
        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileChange}
        />

        {/* Toast */}
        <div className={`toast ${toastVisible ? 'show' : ''}`}>{toastMessage}</div>

        {/* DevFix Panel (dev-only) */}
        <DevFixPanel />

        {/* Global Timer Widget (shows when timer is running) */}
        <GlobalTimerWidget />

        {children}

        {/* Global Entity Modal - renders entity widgets from any URL */}
        <GlobalEntityModal />
      </div>
    </AppShellContext.Provider>
  );
}

/**
 * Global Entity Modal - Detects entity IDs in URL and renders appropriate widget
 */
function GlobalEntityModal() {
  const { entityId, entityType, isOpen, closeEntity } = useEntityModal();

  if (!isOpen || !entityId || !entityType) {
    return null;
  }

  // Map our EntityType to IdPrefix for renderWidgetByType
  const widgetType = entityType as IdPrefix;

  return renderWidgetByType(widgetType, {
    entityId,
    onClose: closeEntity,
    onSave: closeEntity,
  });
}

/**
 * Re-export view config for use in other components
 */
export { VIEW_CONFIG };
